import i18n from '@/i18n';
import { baseApi } from '@/store/api/base-api';
import { AUTH_ENDPOINTS } from '@/services/api/endpoints';
import type { ApiSuccessResponse } from '@/types/api.types';
import type { AuthUser } from '@/types/auth.types';
import type {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  LoginResponseData,
  ResetPasswordRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from '../types/auth.types';

// Backend wraps every payload in { success, message, data, errors, meta } — unwrap `data` here
// so the rest of the app deals in plain camelCase shapes.
export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: ({ rememberMe: _rememberMe, ...body }) => ({ url: AUTH_ENDPOINTS.LOGIN, method: 'POST', data: body }),
      transformResponse: (response: ApiSuccessResponse<LoginResponseData>): LoginResponse => ({
        user: response.data.user,
        accessToken: response.data.access_token,
      }),
      invalidatesTags: ['Profile'],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({ url: AUTH_ENDPOINTS.LOGOUT, method: 'POST' }),
      invalidatesTags: ['Profile'],
    }),
    logoutAll: builder.mutation<void, void>({
      query: () => ({ url: AUTH_ENDPOINTS.LOGOUT_ALL, method: 'POST' }),
      invalidatesTags: ['Profile'],
    }),
    forgotPassword: builder.mutation<void, ForgotPasswordRequest>({
      query: (body) => ({ url: AUTH_ENDPOINTS.FORGOT_PASSWORD, method: 'POST', data: body }),
    }),
    resetPassword: builder.mutation<void, ResetPasswordRequest>({
      query: (body) => ({ url: AUTH_ENDPOINTS.RESET_PASSWORD, method: 'POST', data: body }),
    }),
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (body) => ({ url: AUTH_ENDPOINTS.CHANGE_PASSWORD, method: 'POST', data: body }),
    }),
    resendVerification: builder.mutation<void, void>({
      query: () => ({ url: AUTH_ENDPOINTS.EMAIL_RESEND, method: 'POST' }),
    }),
    verifyEmail: builder.mutation<VerifyEmailResponse, VerifyEmailRequest>({
      query: ({ id, hash, expires, signature }) => ({
        url: AUTH_ENDPOINTS.EMAIL_VERIFY(id, hash),
        method: 'GET',
        params: { expires, signature },
      }),
      transformResponse: (response: ApiSuccessResponse<null>): VerifyEmailResponse => ({
        message: response.message ?? i18n.t('auth.verifyEmail.verifiedDefault'),
      }),
      invalidatesTags: ['Profile'],
    }),
    getProfile: builder.query<AuthUser, void>({
      query: () => ({ url: AUTH_ENDPOINTS.PROFILE }),
      transformResponse: (response: ApiSuccessResponse<AuthUser>) => response.data,
      providesTags: ['Profile'],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useLogoutAllMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useResendVerificationMutation,
  useVerifyEmailMutation,
  useGetProfileQuery,
} = authApi;
